import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, forwardRef, signal, OnInit, inject } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslationService } from '@newroom-connect/library/services';

import { InputComponent } from '../input.component';
import { IconComponent } from '../../icon/icon.component';

export interface InputCardSelectOption<T = string> {
  value: T;
  label: string;
  icon: string;
  description?: string;
  disabled?: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, IconComponent],
  selector: 'nrc-input-card-select',
  templateUrl: './input-card-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCardSelectComponent),
      multi: true
    }
  ]
})
export class InputCardSelectComponent<T = string> extends InputComponent<T | null> implements OnInit {
  private readonly translationService = inject(TranslationService);

  @Input() public options: InputCardSelectOption<T>[] = [];
  @Input() public gridColumns: 1 | 2 | 3 | 4 = 3;
  @Input() public isTouched = false;
  @Input() public loading = false;
  @Input() public loadingText = this.translationService.translate('COMPONENTS.INPUTS.INPUT_CARD_SELECT.LOADING');
  @Input() public hint?: string;
  @Input() public invalidHint?: string;

  public selectedValue = signal<T | null>(null);

  /**
   * Component initialization.
   */
  public override ngOnInit(): void {
    super.ngOnInit();

    this.selectedValue.set(this.formControl.value);
  }

  /**
   * Write value to the component.
   *
   * @param value - The value to write.
   */
  public override writeValue(value: T | null): void {
    super.writeValue(value);

    this.selectedValue.set(value);
  }

  /**
   * Select an option and update the form control.
   *
   * @param option - The option to select.
   */
  public selectOption(option: InputCardSelectOption<T>): void {
    if (option.disabled || this.formControl.disabled) {
      return;
    }

    this.selectedValue.set(option.value);
    this.formControl.setValue(option.value);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  /**
   * Check if an option is selected.
   *
   * @param value - The value to check.
   *
   * @returns True if the option is selected.
   */
  public isOptionSelected(value: T): boolean {
    return this.selectedValue() === value;
  }
}
